import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { useProfile } from '../contexts/ProfileContext';
import { getHomepagePreference, getHomepageRoute } from '../utils/HomepagePreference';
import Homepage from './Homepage';

/**
 * HomepageRedirect Component
 * 
 * Handles redirecting users from the root path (/) to their preferred homepage.
 * Only redirects if:
 * - User is authenticated
 * - User has a current profile
 * - User has a homepage preference set (not default 'movies')
 * - No channel query parameter (to preserve deep links)
 */
const HomepageRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, user } = useStreamAuth();
  const { currentProfile } = useProfile();

  useEffect(() => {
    // Only redirect if user is authenticated and has a profile
    if (!isAuthenticated || !currentProfile) {
      return;
    }

    // Don't redirect if there's a channel query parameter (preserve deep links)
    const searchParams = new URLSearchParams(location.search);
    if (searchParams.has('channel')) {
      return;
    }

    // Get user's homepage preference
    const profileId = currentProfile._id || user?.sub;
    const preference = getHomepagePreference(profileId);
    
    // Only redirect if preference is not 'movies' (default)
    // If preference is 'movies', we're already on the right page
    if (preference !== 'movies') {
      const preferredRoute = getHomepageRoute(preference);
      // Only redirect if we're actually on the root path
      if (location.pathname === '/') {
        navigate(preferredRoute, { replace: true });
      }
    }
  }, [isAuthenticated, currentProfile, user, location.pathname, location.search, navigate]);

  // Render Homepage (default page) while checking/redirecting
  return <Homepage />;
};

export default HomepageRedirect;

